import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/joy";
import { useParams } from "react-router-dom";
import Countdown from "react-countdown";
import Confetti from "react-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { VotingBar, LiveHearts } from "../components/Misc";
import { useTranslation } from "react-i18next";
import SeoHelmet from "../components/SeoHelmet";

// --- ŚMIESZNE TEKSTY Z TŁUMACZEŃ ---
const getFunnyTexts = (t) => t("reveal.funnyTexts", { returnObjects: true });

// Bajkowe tło 
const Bkg = ({ revealed }) => (
  <motion.div
    initial={{ opacity: 0.7 }}
    animate={{
      opacity: revealed ? 0.7 : 1,
      background: revealed
        ? "linear-gradient(120deg, #fce4ec 0%, #d0eaff 100%)"
        : [
            "linear-gradient(120deg, #ffd6ec 0%, #d0eaff 100%)",
            "linear-gradient(120deg, #fce4ec 0%, #b0d9ff 100%)",
            "linear-gradient(120deg, #fce4ec 0%, #d0eaff 100%)"
          ]
    }}
    transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: -1,
      width: "100vw",
      height: "100vh"
    }}
  />
);

// Animowana cyfra
const Bubble = ({ value, label }) => (
  <motion.div
    initial={{ scale: 0.8, y: 10, opacity: 0.7 }}
    animate={{ scale: 1.1, y: 0, opacity: 1 }}
    transition={{ type: "spring", stiffness: 120 }}
    style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      margin: "0 12px"
    }}
  >
    <motion.div
      style={{
        background: "#fff",
        color: "#2d3c88",
        borderRadius: "2rem",
        fontWeight: 800,
        fontSize: "2.5rem",
        minWidth: 60,
        minHeight: 60,
        boxShadow: "0 4px 18px 2px #abc9ff3a, 0 1.5px #bcd",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
      whileHover={{ scale: 1.15, rotate: [0, 2, -2, 0] }}
    >
      {value}
    </motion.div>
    <Typography level="body-sm" sx={{ color: "#5e79b8", mt: 1 }}>
      {label}
    </Typography>
  </motion.div>
);

const Reveal = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [showGender, setShowGender] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [textIdx, setTextIdx] = useState(0);

  // --- FETCH EVENT ---
  useEffect(() => {
    fetch(`https://revealoo.com/api/get_event.php?id=${id}`)
      .then((res) => {
        if (!res.ok) throw new Error(t("reveal.eventNotFound"));
        return res.json();
      })
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setEvent(data);
      })
      .catch((err) => setError(err.message));

    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line
  }, [id, t]);

  // --- ROTUJ ŚMIESZNE TEKSTY ---
  const funnyTexts = getFunnyTexts(t);
  useEffect(() => {
    if (showGender) return;
    const interval = setInterval(() => {
      setTextIdx((prev) => (prev + 1) % funnyTexts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showGender, funnyTexts.length]);

  // --- ANIMOWANY LICZNIK ---
  const renderCountdown = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      setTimeout(() => setShowGender(true), 800);
      return null;
    } else {
      return (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.5 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={textIdx}
              initial={{ opacity: 0, scale: 0.8, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.7, y: -24 }}
              transition={{ type: "spring", duration: 0.5 }}
              style={{ marginBottom: 24, fontSize: 22, fontWeight: 700, color: "#2d3c88" }}
            >
              {funnyTexts[textIdx]}
            </motion.div>
          </AnimatePresence>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Bubble value={days} label={t("reveal.days")} />
            <Bubble value={hours} label={t("reveal.hours")} />
            <Bubble value={minutes} label={t("reveal.minutes")} />
            <Bubble value={seconds} label={t("reveal.seconds")} />
          </Box>
        </motion.div>
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        textAlign: "center",
        position: "relative"
      }}
    >
      <SeoHelmet
  title={
    event?.baby_name
      ? t("meta.revealTitleWithName", { name: event.baby_name })
      : t("meta.revealTitle")
  }
  description={
    event?.baby_name
      ? t("meta.revealDescWithName", { name: event.baby_name })
      : t("meta.revealDesc")
  }
/>
      <Bkg revealed={showGender} />
      {error ? (
        <>
          <Typography level="h2" sx={{ mb: 2 }}>
            😢 {t("reveal.errorTitle")}
          </Typography>
          <Typography level="body-lg">{error}</Typography>
        </>
      ) : !event ? (
        <Typography level="h2">{t("reveal.loading")}</Typography>
      ) : showGender ? (
        <>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={true}
            numberOfPieces={500}
            colors={
              event.gender === "boy"
                ? ["#1976d2", "#2196f3", "#90caf9", "#bbdefb"]
                : ["#ec407a", "#f06292", "#f8bbd0", "#fce4ec"]
            }
          />
          <AnimatePresence>
            <motion.div
              key="reveal"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, type: "spring" }}
              style={{ marginTop: 40 }}
            >
              <Typography level="h1" gutterBottom>
                {event.gender === "boy"
                  ? t("reveal.boy")
                  : event.gender === "girl"
                  ? t("reveal.girl")
                  : t("reveal.unknown")}
              </Typography>
              {event.baby_name && (
                <Typography level="body-lg" sx={{ mt: 1 }}>
                  {t("reveal.babyName", { name: event.baby_name })}
                </Typography>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      ) : (
        <>
          <Countdown date={new Date(event.datetime)} renderer={renderCountdown} />
          {event && (
            <>
              <VotingBar eventId={id} />
              <LiveHearts eventId={id} />
            </>
          )}
          <motion.div
            initial={{ backgroundPosition: "0% 50%" }}
            animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              width: "100vw",
              height: 180,
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(90deg, #ec407a 0%, #fce4ec 30%, #d0eaff 70%, #1976d2 100%)",
              backgroundSize: "300% 100%",
              opacity: 0.80,
              filter: "blur(34px)",
              WebkitMaskImage: "linear-gradient(to top, black 70%, transparent 100%)",
              maskImage: "linear-gradient(to top, black 70%, transparent 100%)",
            }}
          />
        </>
      )}
    </Box>
  );
};

export default Reveal;
